import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { usePatchContentItem } from '../../hooks/useContent';
import { REVIEWERS } from './reviewers';

interface ReviewerPickerProps {
  itemId: string;
  /** Called after a successful send so the parent can refetch / close. */
  onSent?: () => void;
}

/**
 * Self-contained picker UI for sending a content item to sequential review.
 * Reviewers are DM'd one at a time in selection order; the API only fires
 * the first DM here — the rest are triggered as each prior reviewer ticks
 * their Slack button.
 */
export function ReviewerPicker({ itemId, onSent }: ReviewerPickerProps) {
  const patchItem = usePatchContentItem();
  const [open, setOpen] = useState(false);
  const [selectedReviewerIds, setSelectedReviewerIds] = useState<string[]>([]);
  const [reviewNote, setReviewNote] = useState('');
  const [slackError, setSlackError] = useState<string | null>(null);

  function toggleReviewer(userId: string) {
    setSelectedReviewerIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  function reset() {
    setOpen(false);
    setSelectedReviewerIds([]);
    setReviewNote('');
  }

  async function handleSend() {
    if (selectedReviewerIds.length === 0) return;
    setSlackError(null);
    const reviewers = selectedReviewerIds
      .map((id) => REVIEWERS.find((r) => r.id === id))
      .filter((r): r is (typeof REVIEWERS)[number] => Boolean(r))
      .map((r) => ({ userId: r.id, name: r.name, email: r.email }));

    const result = await patchItem.mutateAsync({
      id: itemId,
      updates: {
        status: 'review',
        reviewers,
        ...(reviewNote.trim() ? { reviewNote: reviewNote.trim() } : {}),
      },
    });
    const notify = (result as unknown as Record<string, unknown>).slackNotify as
      | { sent: string[]; failed: { email: string; reason: string }[]; error?: string }
      | undefined;
    if (notify?.error) {
      setSlackError(notify.error);
      return; // Keep picker open so the user can see the error and retry
    }
    if (notify?.failed?.length) {
      setSlackError(notify.failed.map((f) => `${f.email}: ${f.reason}`).join('\n'));
      return;
    }
    reset();
    onSent?.();
  }

  if (!open) {
    return (
      <div className="space-y-2">
        <Button size="sm" onClick={() => setOpen(true)}>
          Send to Review
        </Button>
        {slackError && (
          <div className="border border-red-300 bg-red-50 rounded-md p-3 text-body-xs text-red-700 whitespace-pre-line">
            {slackError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 border border-border rounded-md p-3 bg-surface-subtle">
      <div>
        <p className="text-body-xs font-medium text-text-primary">Select reviewers in review order</p>
        <p className="text-body-xs text-text-tertiary">
          Click reviewers in the order you want them. The first gets a Slack DM right away;
          the next is DM'd automatically once the prior one clicks "Mark as reviewed" in OS.
        </p>
      </div>
      <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-md p-2 bg-white">
        {REVIEWERS.map((r) => {
          const orderIdx = selectedReviewerIds.indexOf(r.id);
          const isSelected = orderIdx >= 0;
          return (
            <label
              key={r.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-secondary cursor-pointer"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleReviewer(r.id)}
                className="rounded border-border"
              />
              {isSelected && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold">
                  {orderIdx + 1}
                </span>
              )}
              <span className="text-body-xs text-text-primary">{r.name}</span>
            </label>
          );
        })}
      </div>
      {selectedReviewerIds.length > 0 && (
        <div className="text-body-xs text-text-secondary">
          <span className="font-medium">Review order:</span>{' '}
          {selectedReviewerIds
            .map((id) => REVIEWERS.find((r) => r.id === id)?.name ?? id)
            .map((name, i) => `${i + 1}. ${name}`)
            .join(' → ')}
        </div>
      )}
      <textarea
        value={reviewNote}
        onChange={(e) => setReviewNote(e.target.value)}
        placeholder="Add a note for reviewers (optional)"
        maxLength={500}
        rows={2}
        className="w-full text-body-xs border border-border rounded-md p-2 resize-none placeholder:text-text-tertiary bg-white"
      />
      {slackError && (
        <div className="border border-red-300 bg-red-50 rounded-md p-3 text-body-xs text-red-700 whitespace-pre-line">
          {slackError}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleSend}
          disabled={selectedReviewerIds.length === 0 || patchItem.isPending}
        >
          {patchItem.isPending
            ? 'Sending...'
            : `Send to ${selectedReviewerIds.length} reviewer${selectedReviewerIds.length !== 1 ? 's' : ''}`}
        </Button>
        <Button size="sm" variant="secondary" onClick={reset}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
